import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const MandatoryTrainingELearning = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">How mandatory training works</Typography>
      <Typography>
        Each organisation keeps its own list of mandatory training courses and
        decides which roles need each one. A member of staff is in date for a
        course when they hold evidence that has not expired. Evidence can be:
      </Typography>
      <Typography component="ul" sx={{ pl: 3, m: 0 }}>
        <li>a certificate they upload, once Personnel has approved it</li>
        <li>a record entered by Personnel</li>
        <li>an e-learning course completed in AmbuNet (Premium and above)</li>
      </Typography>
      <Typography>
        The course list, the role mapping, certificate upload and the training
        matrix are available on every plan. The e-learning courses need
        Premium or above.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Setting up courses and who needs them</Typography>
      <Typography>
        Go to{' '}
        <DocLink
          url="/site-configuration/mandatory-training"
          text="Configuration > Employees > Mandatory Training"
        />
        . Add or edit a course, choose how often it must be refreshed, and tick
        the roles that need it.
      </Typography>
      <Alert severity="info" variant="outlined">
        Setting up courses needs the <strong>Governance</strong> team, while
        the training matrix, reminders and chasing are open to{' '}
        <strong>Personnel or Governance</strong> — so HR can chase gaps without
        being able to change what counts as mandatory.
      </Alert>
      <Typography>
        Courses that have an AmbuNet e-learning course show an{' '}
        <strong>E-learning</strong> label. You can add an e-learning course to
        your list from the panel on that page, or link one of your existing
        courses to it so that staff history stays on one course. For a linked
        course you can also:
      </Typography>
      <Typography component="ul" sx={{ pl: 3, m: 0 }}>
        <li>
          choose the level each role needs, where a course has more than one
          level
        </li>
        <li>
          shorten the refresher period (it can be made shorter than the
          course&apos;s standard period, but not longer)
        </li>
        <li>
          let staff who have completed the course before take the assessment
          first when they refresh
        </li>
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Taking a course</Typography>
      <Typography>
        Staff open <DocLink url="/mandatory-training" text="My Training" />,
        choose a course and select <strong>Start course</strong>. The course
        opens in its own screen, without the usual menus.
      </Typography>
      <Typography component="ul" sx={{ pl: 3, m: 0 }}>
        <li>
          Progress saves on every screen. Staff can stop and carry on later, on
          any device.
        </li>
        <li>
          Next unlocks once a screen has been read or its activity is done, so
          nobody can skip to the end. Finished screens can be revisited from
          the course outline.
        </li>
        <li>
          Every activity can be done with a keyboard or a screen reader.
        </li>
        <li>
          The course ends with an assessment. The pass mark is 80% and is shown
          before the assessment starts. Questions and answer order are
          different on every attempt.
        </li>
        <li>
          After an unsuccessful attempt, the member of staff is shown which
          modules to look at again. They reopen those modules, then take a new
          attempt with a fresh set of questions. There is no limit on attempts.
        </li>
        <li>
          After passing, a short feedback form unlocks the certificate.
        </li>
      </Typography>
      <Alert severity="info" variant="outlined">
        Results from the earlier Fire Safety, Conflict Resolution and Equality
        and Diversity e-learning no longer count. They stay on the staff record
        as history, marked &quot;Not counted&quot;. Staff take the new course,
        or upload a certificate.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Certificates</Typography>
      <Typography>
        Passing a course adds the completion to the staff record straight away,
        with its expiry date, and issues a certificate as a PDF. The
        certificate shows the course and version, the score, the dates and the
        estimated learning time, with the learner&apos;s written reflections on
        the second page.
      </Typography>
      <Typography>
        Each certificate has a verification code and a QR code. Anyone with
        the code can open the verification page, which shows the name,
        organisation, course and dates and whether the certificate is still
        valid. Personnel can withdraw a certificate from the staff record; it
        then shows as withdrawn and stops counting.
      </Typography>
      <Typography>
        Some courses have a practical element that an online course cannot
        assess, such as moving and handling or CPR. Their certificates say
        that they cover the knowledge part only. Record the practical
        sign-off on the staff record (see below).
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">The training matrix</Typography>
      <Typography>
        <DocLink url="/employees/mandatory-training" text="Training matrix" />{' '}
        shows every active member of staff against every course: in date,
        expiring soon (the last six weeks), overdue, not completed, or
        awaiting approval. Filter by role or status, or show only staff with
        gaps.
      </Typography>
      <Typography component="ul" sx={{ pl: 3, m: 0 }}>
        <li>
          Select a course heading to remind the staff who are overdue or not
          complete, or to assign an e-learning course to one person with a due
          date.
        </li>
        <li>
          Export the matrix as a CSV or PDF for inspections and commissioners.
        </li>
        <li>Select a name to open that person&apos;s training record.</li>
      </Typography>
      <Typography>
        A completion at a higher level also counts for the lower levels. For
        example, Safeguarding Adults Level 3 covers Levels 1 and 2.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">A person&apos;s training record</Typography>
      <Typography>
        The <strong>Mandatory Training</strong> tab on an employee&apos;s page
        lists each course with its status and history. For e-learning it also
        shows courses in progress, every assessment attempt, certificates, and
        practical sign-offs.
      </Typography>
      <Typography>
        To record a practical sign-off, choose{' '}
        <strong>Record practical sign-off</strong>, tick the checklist items
        the person met, and add the date and any notes or evidence. Practical
        sign-offs are your organisation&apos;s own record and are separate
        from the e-learning certificate.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Reminders and shift planning</Typography>
      <Typography>
        Staff are reminded before required training expires at the stages your
        organisation configures — by default 90, 30 and 7 days before, and on
        the day it expires. They choose how they receive reminders in{' '}
        <DocLink url="/my-account" text="My Account" />.
      </Typography>
      <Typography>
        When you assign someone to a shift, AmbuNet warns you if their
        required training is overdue or not complete. If{' '}
        <strong>Prevent applications with expired mandatory training</strong>{' '}
        is switched on in{' '}
        <DocLink url="/site-configuration/shifts" text="Configuration > Shifts" />
        , the assignment is blocked and staff cannot request shifts until
        their training is in date.
      </Typography>
    </Stack>
  </Stack>
)

export default MandatoryTrainingELearning
